import { useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useImportFormats, useValidateImport, useImportRecords } from "@/api/hooks";
import type { ImportValidationResult, ImportReport } from "@/api/hooks/useCatalog";

function formatArkSummary(assigned: number, unresolved: number): string {
  const added =
    assigned > 0
      ? `ajouté à ${assigned.toLocaleString()} notice${assigned > 1 ? "s" : ""}.`
      : "aucun ajouté.";
  if (unresolved === 0) {
    return added;
  }
  return `${added} ${unresolved.toLocaleString()} notice${unresolved > 1 ? "s" : ""} sans correspondance unique au catalogue de la BnF (ou service indisponible).`;
}

export function RecordImportPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [validation, setValidation] = useState<ImportValidationResult | null>(null);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const { data: formats } = useImportFormats();
  const validateMutation = useValidateImport();
  const importMutation = useImportRecords();

  const handleFileSelect = useCallback((selectedFile: File) => {
    setFile(selectedFile);
    setValidation(null);
    setReport(null);
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      if (selectedFile) {
        handleFileSelect(selectedFile);
      }
    },
    [handleFileSelect]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(false);
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) {
        handleFileSelect(droppedFile);
      }
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleValidate = useCallback(async () => {
    if (!file) return;

    try {
      const result = await validateMutation.mutateAsync(file);
      setValidation(result);
    } catch (error) {
      setValidation({
        valid: false,
        error: error instanceof Error ? error.message : "Erreur de validation",
      });
    }
  }, [file, validateMutation]);

  const handleImport = useCallback(
    async (dryRun: boolean) => {
      if (!file) return;

      try {
        const result = await importMutation.mutateAsync({ file, dryRun });
        setReport(result.report);

        if (!dryRun && result.success && result.report.successCount > 0) {
          // Success toast could be shown here
        }
      } catch {
        setReport(null);
        // Error handling - could show a toast
      }
    },
    [file, importMutation]
  );

  const supportedExtensions = formats?.["unimarc"]?.extensions ?? ["mrc", "iso", "dat", "elc"];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <nav className="mb-2 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Link to="/catalog" className="hover:text-blue-600">
              Catalogue
            </Link>
            <span>/</span>
            <span>Import</span>
          </nav>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Importer des notices
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Importez des notices bibliographiques depuis un fichier UNIMARC ou MARC21
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main content */}
        <div className="space-y-6 lg:col-span-2">
          {/* File upload */}
          <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <h2 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
              Fichier source
            </h2>

            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              className={`cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
                isDragOver
                  ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                  : "border-gray-300 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500"
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={supportedExtensions.map((e) => `.${e}`).join(",")}
                onChange={handleFileChange}
                className="hidden"
              />

              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 48 48"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M24 32V16m0 0l-6 6m6-6l6 6M8 24c0-8.8 7.2-16 16-16s16 7.2 16 16-7.2 16-16 16S8 32.8 8 24z"
                />
              </svg>

              <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                <span className="font-medium text-blue-600 dark:text-blue-400">
                  Cliquez pour sélectionner
                </span>{" "}
                ou glissez-déposez un fichier
              </p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                Formats acceptés : {supportedExtensions.map((e) => `.${e}`).join(", ")}
              </p>

              {file && (
                <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-blue-100 px-3 py-1 text-sm text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  {file.name} ({(file.size / 1024).toFixed(1)} Ko)
                </div>
              )}
            </div>

            {/* Actions */}
            {file && (
              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => void handleValidate()}
                  disabled={validateMutation.isPending}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                >
                  {validateMutation.isPending ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
                      Validation...
                    </>
                  ) : (
                    <>
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Valider le fichier
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => void handleImport(true)}
                  disabled={importMutation.isPending}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                >
                  Simuler l'import
                </button>

                <button
                  type="button"
                  onClick={() => void handleImport(false)}
                  disabled={importMutation.isPending}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {importMutation.isPending ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                      Import en cours...
                    </>
                  ) : (
                    <>
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                        />
                      </svg>
                      Importer les notices
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Validation result */}
          {validation && (
            <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
              <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
                Résultat de la validation
              </h3>

              <div
                className={`rounded-lg p-4 ${
                  validation.valid
                    ? "bg-green-50 dark:bg-green-900/20"
                    : "bg-red-50 dark:bg-red-900/20"
                }`}
              >
                <div className="flex items-center gap-2">
                  {validation.valid ? (
                    <>
                      <svg
                        className="h-5 w-5 text-green-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="font-medium text-green-800 dark:text-green-200">
                        Fichier valide
                      </span>
                    </>
                  ) : (
                    <>
                      <svg
                        className="h-5 w-5 text-red-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="font-medium text-red-800 dark:text-red-200">
                        Fichier invalide
                      </span>
                    </>
                  )}
                </div>

                {validation.error && (
                  <p className="mt-2 text-sm text-red-700 dark:text-red-300">{validation.error}</p>
                )}
              </div>

              {validation.valid && (
                <dl className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Notices estimées</dt>
                    <dd className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                      {validation.estimatedRecords?.toLocaleString() ?? "-"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Taille du fichier</dt>
                    <dd className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                      {validation.fileSize ? `${(validation.fileSize / 1024).toFixed(1)} Ko` : "-"}
                    </dd>
                  </div>
                  {validation.sample?.title && (
                    <div className="sm:col-span-2">
                      <dt className="text-sm text-gray-500 dark:text-gray-400">
                        Aperçu (1ère notice)
                      </dt>
                      <dd className="mt-1 text-gray-900 dark:text-white">
                        <span className="font-medium">{validation.sample.title}</span>
                        {validation.sample.isbn && (
                          <span className="ml-2 text-sm text-gray-500">
                            (ISBN: {validation.sample.isbn})
                          </span>
                        )}
                      </dd>
                    </div>
                  )}
                </dl>
              )}

              {validation.warnings && validation.warnings.length > 0 && (
                <div className="mt-4 rounded-lg bg-yellow-50 p-4 dark:bg-yellow-900/20">
                  <h4 className="font-medium text-yellow-800 dark:text-yellow-200">
                    Avertissements
                  </h4>
                  <ul className="mt-2 list-inside list-disc text-sm text-yellow-700 dark:text-yellow-300">
                    {validation.warnings.map((warning, idx) => (
                      <li key={idx}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Import report */}
          {report && (
            <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
              <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
                Rapport d'import
              </h3>

              <div className="grid gap-4 sm:grid-cols-4">
                <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
                  <p className="text-3xl font-bold text-blue-900 dark:text-blue-100">
                    {report.totalProcessed.toLocaleString()}
                  </p>
                  <p className="text-sm text-blue-700 dark:text-blue-300">Notices traitées</p>
                </div>
                <div className="rounded-lg bg-green-50 p-4 dark:bg-green-900/20">
                  <p className="text-3xl font-bold text-green-900 dark:text-green-100">
                    {report.createdCount.toLocaleString()}
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-300">Créées</p>
                </div>
                <div className="rounded-lg bg-cyan-50 p-4 dark:bg-cyan-900/20">
                  <p className="text-3xl font-bold text-cyan-900 dark:text-cyan-100">
                    {report.updatedCount.toLocaleString()}
                  </p>
                  <p className="text-sm text-cyan-700 dark:text-cyan-300">Mises à jour</p>
                </div>
                <div className="rounded-lg bg-red-50 p-4 dark:bg-red-900/20">
                  <p className="text-3xl font-bold text-red-900 dark:text-red-100">
                    {report.errorCount.toLocaleString()}
                  </p>
                  <p className="text-sm text-red-700 dark:text-red-300">Erreurs</p>
                </div>
              </div>

              {/* Transition bibliographique conformity */}
              {report.successCount > 0 && (
                <p className="mt-4 rounded-lg border border-gray-200 px-4 py-3 text-sm text-gray-700 dark:border-gray-700 dark:text-gray-300">
                  <span className="font-medium text-gray-900 dark:text-white">
                    Transition bibliographique :
                  </span>{" "}
                  {report.rdaInferredCount > 0
                    ? `${report.rdaInferredCount.toLocaleString()} notice${report.rdaInferredCount > 1 ? "s" : ""} sur ${report.successCount.toLocaleString()} complétée${report.rdaInferredCount > 1 ? "s" : ""} avec des types de contenu, de médiation ou de support déduits (zones 181 à 183). Les types déclarés par la notice source sont conservés.`
                    : "aucun type de contenu, de médiation ou de support (zones 181 à 183) n’a eu à être déduit."}
                </p>
              )}

              {/* BnF ARK (003), only when the import looked some up */}
              {report.arkAssignedCount + report.arkUnresolvedCount > 0 && (
                <p className="mt-4 rounded-lg border border-gray-200 px-4 py-3 text-sm text-gray-700 dark:border-gray-700 dark:text-gray-300">
                  <span className="font-medium text-gray-900 dark:text-white">
                    Identifiant ARK BnF (zone 003) :
                  </span>{" "}
                  {formatArkSummary(report.arkAssignedCount, report.arkUnresolvedCount)}
                </p>
              )}

              {/* Progress bar */}
              <div className="mt-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Taux de succès</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {report.successRate.toFixed(1)}%
                  </span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                  <div
                    className="h-full bg-green-500 transition-all"
                    style={{ width: `${String(report.successRate)}%` }}
                  />
                </div>
              </div>

              {/* Sample titles */}
              {report.successes && report.successes.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Exemples de notices traitées
                  </h4>
                  <ul className="mt-2 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                    {report.successes.slice(0, 5).map((item) => (
                      <li key={item.index} className="flex items-center gap-2 truncate">
                        <span
                          className={`inline-flex rounded px-1.5 py-0.5 text-xs font-medium ${
                            item.action === "created"
                              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                              : "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300"
                          }`}
                        >
                          {item.action === "created" ? "Créée" : "MAJ"}
                        </span>
                        <span className="truncate">{item.title}</span>
                      </li>
                    ))}
                    {report.successes.length > 5 && (
                      <li className="text-gray-400 italic">
                        ... et {report.successes.length - 5} autres
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {/* Errors */}
              {report.errors && report.errors.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-red-700 dark:text-red-300">
                    Erreurs ({report.errors.length})
                  </h4>
                  <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto text-sm text-red-600 dark:text-red-400">
                    {report.errors.slice(0, 20).map((error, idx) => (
                      <li key={idx} className="border-b border-red-100 py-1 dark:border-red-800">
                        {error}
                      </li>
                    ))}
                    {report.errors.length > 20 && (
                      <li className="py-1 italic">
                        ... et {report.errors.length - 20} autres erreurs
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {/* Actions after import */}
              {report.successCount > 0 && (
                <div className="mt-6 flex gap-3">
                  <button
                    type="button"
                    onClick={() => navigate("/catalog")}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                  >
                    Voir le catalogue
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setFile(null);
                      setValidation(null);
                      setReport(null);
                    }}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                  >
                    Nouvel import
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar - Help */}
        <div className="space-y-6">
          <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <h2 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
              Formats supportés
            </h2>

            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white">
                  UNIMARC / MARC21 ISO 2709
                </h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  Format standard d'échange de notices bibliographiques utilisé par les
                  bibliothèques.
                </p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {supportedExtensions.map((ext) => (
                    <span
                      key={ext}
                      className="rounded bg-gray-100 px-2 py-0.5 text-xs dark:bg-gray-700"
                    >
                      .{ext}
                    </span>
                  ))}
                </div>
              </div>

              <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
                <h4 className="font-medium text-blue-800 dark:text-blue-200">
                  Sources compatibles
                </h4>
                <ul className="mt-2 list-inside list-disc text-sm text-blue-700 dark:text-blue-300">
                  <li>Electre (.elc)</li>
                  <li>BnF Data</li>
                  <li>Export SIGB (PMB, Koha...)</li>
                  <li>WorldCat</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <h2 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">Conseils</h2>

            <div className="space-y-4 text-sm text-gray-600 dark:text-gray-400">
              <div className="flex gap-3">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                  1
                </span>
                <p>
                  <strong className="text-gray-900 dark:text-white">Validez d'abord</strong> votre
                  fichier pour vérifier sa structure et estimer le nombre de notices.
                </p>
              </div>

              <div className="flex gap-3">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                  2
                </span>
                <p>
                  <strong className="text-gray-900 dark:text-white">Simulez l'import</strong> pour
                  voir le résultat sans modifier la base.
                </p>
              </div>

              <div className="flex gap-3">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                  3
                </span>
                <p>
                  <strong className="text-gray-900 dark:text-white">Lancez l'import</strong> une
                  fois satisfait du résultat de la simulation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RecordImportPage;
